import React, { useState, useEffect } from 'react';
import { withStyles } from '@mui/styles';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DocumentMSStyle from './DocumentMSStyle';
import { filterActivityLogs } from './dmsApi';

const DocumentHistory = (props) => {
  const { classes, open, onClose, document, onSuccess } = props;
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && document) {
      fetchHistory();
    }
  }, [open, document]);

  const fetchHistory = async () => {
    if (!document) return;
    
    try {
      setLoading(true);
      setError(null);
      const documentId = document.id || document.record_id;
      const filters = {
        document_id: documentId,
      };
      const data = await filterActivityLogs(filters);
      setHistory(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Failed to load document history');
      console.error('History error:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  };

  const getActivityTypeLabel = (type) => {
    if (!type) return 'Activity';
    return type.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const documentName = document?.document_name || 'Document';

  return (
    <Dialog
      open={Boolean(open)}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: '16px',
          fontFamily: 'Montserrat',
        },
      }}
    >
      <DialogTitle
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '24px 24px 16px 24px',
          borderBottom: '1px solid #E5E7EB',
        }}
      >
        <Box>
          <Typography
            style={{
              fontSize: '24px',
              fontWeight: 700,
              color: '#131C4C',
              fontFamily: 'Montserrat',
              marginBottom: '4px',
            }}
          >
            Document History
          </Typography>
          <Typography
            style={{
              fontSize: '14px',
              color: '#6E759A',
              fontFamily: 'Montserrat',
            }}
          >
            {documentName}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          style={{
            color: '#6E759A',
            padding: '4px',
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent style={{ padding: '24px' }}>
        {loading ? (
          <Box style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box style={{ textAlign: 'center', padding: '40px' }}>
            <Typography style={{ color: '#DC2626', fontSize: '14px', fontFamily: 'Montserrat' }}>
              {error}
            </Typography>
          </Box>
        ) : history.length === 0 ? (
          <Box style={{ textAlign: 'center', padding: '40px' }}>
            <Typography style={{ color: '#6E759A', fontSize: '14px', fontFamily: 'Montserrat' }}>
              No history available for this document
            </Typography>
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            style={{
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
              overflow: 'hidden',
            }}
          >
            <Table className={classes.table}>
              <TableHead>
                <TableRow>
                  <TableCell className={classes.tableHeader} style={{ whiteSpace: 'nowrap' }}>
                    Timestamp
                  </TableCell>
                  <TableCell className={classes.tableHeader} style={{ whiteSpace: 'nowrap' }}>
                    Type
                  </TableCell>
                  <TableCell className={classes.tableHeader}>Description</TableCell>
                  <TableCell className={classes.tableHeader}>User</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((log, index) => (
                  <TableRow key={index} className={classes.tableRow}>
                    <TableCell className={classes.tableCell} style={{ whiteSpace: 'nowrap' }}>
                      <Typography
                        style={{
                          fontSize: '14px',
                          color: '#131C4C',
                          fontFamily: 'Montserrat',
                        }}
                      >
                        {formatTimestamp(log.timestamp)}
                      </Typography>
                    </TableCell>
                    <TableCell className={classes.tableCell} style={{ whiteSpace: 'nowrap' }}>
                      <Typography
                        style={{
                          fontSize: '14px',
                          color: '#131C4C',
                          fontFamily: 'Montserrat',
                          fontWeight: 500,
                        }}
                      >
                        {getActivityTypeLabel(log.activity_type)}
                      </Typography>
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      <Typography
                        style={{
                          fontSize: '14px',
                          color: '#131C4C',
                          fontFamily: 'Montserrat',
                        }}
                      >
                        {log.description || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      <Typography
                        style={{
                          fontSize: '14px',
                          color: '#131C4C',
                          fontFamily: 'Montserrat',
                        }}
                      >
                        {log.user || log.assigned_by_name || '-'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions
        style={{
          padding: '16px 24px 24px 24px',
          borderTop: '1px solid #E5E7EB',
        }}
      >
        <Button
          onClick={onClose}
          className={classes.buttonSecondary}
          style={{
            textTransform: 'none',
            fontFamily: 'Montserrat',
            fontSize: '14px',
            fontWeight: 500,
            padding: '8px 24px',
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default withStyles(DocumentMSStyle)(DocumentHistory);
